import React, { useState, useEffect } from 'react';
import { getEstadisticas, getPedidos } from '../../services/api';
import {
  imprimirEtiquetasPDF,
  descargarEtiquetasPDF
} from '../../utils/zebraLabelPDF';

function Produccion() {
  const [activeTab, setActiveTab] = useState('mañana');
  const [estadisticas, setEstadisticas] = useState([]);
  const [pedidosDetalle, setPedidosDetalle] = useState([]);
  const [totalPedidos, setTotalPedidos] = useState(0);
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [loading, setLoading] = useState(false);

  // Función helper para obtener fecha sin problema de timezone
  const getFechaLocal = (diasAdelante = 0) => {
    const hoy = new Date();
    hoy.setDate(hoy.getDate() + diasAdelante);
    const year = hoy.getFullYear();
    const month = String(hoy.getMonth() + 1).padStart(2, '0');
    const day = String(hoy.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper para formatear fecha objeto a string YYYY-MM-DD
  const fechaToString = (fecha) => {
    const year = fecha.getFullYear();
    const month = String(fecha.getMonth() + 1).padStart(2, '0');
    const day = String(fecha.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Calcular rango de fechas según período
  const calcularRangoFechas = (periodo) => {
    let inicio, fin;
    const hoy = new Date();

    switch (periodo) {
      case 'hoy':
        // Solo hoy
        inicio = getFechaLocal(0);
        fin = getFechaLocal(0);
        break;

      case 'mañana':
        // Solo mañana
        inicio = getFechaLocal(1);
        fin = getFechaLocal(1);
        break;

      case 'semana':
        // Desde hoy/mañana hasta el domingo de esta semana
        // Si es después del domingo, va al próximo domingo
        const diaSemanaActual = hoy.getDay();
        const mañana = new Date(hoy);
        mañana.setDate(mañana.getDate() + 1);
        
        // Calcular el próximo domingo (o este domingo si es domingo)
        let diasAlDomingo = 0;
        if (diaSemanaActual === 0) {
          // Hoy es domingo, el domingo próximo es en 7 días
          diasAlDomingo = 7;
        } else {
          // Otros días, calcular cuántos días faltan hasta el domingo
          diasAlDomingo = 7 - diaSemanaActual;
        }

        const domingo = new Date(mañana);
        domingo.setDate(mañana.getDate() + diasAlDomingo - 1);

        inicio = fechaToString(mañana);
        fin = fechaToString(domingo);
        break;

      case 'quincena':
        // 15 días desde mañana hasta 15 días adelante
        const mañanaQ = new Date(hoy);
        mañanaQ.setDate(mañana.getDate() + 1);

        const fin15Dias = new Date(mañanaQ);
        fin15Dias.setDate(mañanaQ.getDate() + 13); // +13 = 15 días totales

        inicio = fechaToString(mañanaQ);
        fin = fechaToString(fin15Dias);
        break;

      case 'mes':
        // Desde mañana hasta el final del mes
        const mañanaM = new Date(hoy);
        mañanaM.setDate(hoy.getDate() + 1);
        
        const ultimoDiaMes = new Date(mañanaM.getFullYear(), mañanaM.getMonth() + 1, 0);

        inicio = fechaToString(mañanaM);
        fin = fechaToString(ultimoDiaMes);
        break;

      default:
        inicio = getFechaLocal(1);
        fin = getFechaLocal(1);
    }

    return { inicio, fin };
  };

  // Función para formatear fecha para mostrar
  const formatearFecha = (fechaStr) => {
    const [year, month, day] = fechaStr.split('-');
    const fecha = new Date(year, month - 1, day);
    return fecha.toLocaleDateString('es-AR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  useEffect(() => {
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const { inicio, fin } = calcularRangoFechas(activeTab);
      setFechaInicio(inicio);
      setFechaFin(fin);

      // Si es un solo día, usar endpoint actual
      if (inicio === fin) {
        const statsRes = await getEstadisticas(inicio);
        setEstadisticas(statsRes.data);

        const pedidosRes = await getPedidos({ fecha: inicio, estado: 'confirmado' });
        setPedidosDetalle(pedidosRes.data);
        setTotalPedidos(pedidosRes.data.length);
      } else {
        // Para rangos, cargar todos los días y mantener separado por fecha
        const fechasArray = generarFechasEntre(inicio, fin);
        let todosLosPedidos = [];
        let estadisticasAgregadas = {};

        for (const fecha of fechasArray) {
          try {
            const pedidosRes = await getPedidos({ fecha, estado: 'confirmado' });
            todosLosPedidos = todosLosPedidos.concat(pedidosRes.data);

            const statsRes = await getEstadisticas(fecha);
            
            // Agregar estadísticas globales
            statsRes.data.forEach(item => {
              const key = `${item.categoria_id}-${item.plato_id || item.plato}`;
              if (!estadisticasAgregadas[key]) {
                estadisticasAgregadas[key] = { ...item };
              } else {
                estadisticasAgregadas[key].cantidad += item.cantidad;
              }
            });
          } catch (error) {
            console.error(`Error cargando fecha ${fecha}:`, error);
          }
        }

        setEstadisticas(Object.values(estadisticasAgregadas));
        setPedidosDetalle(todosLosPedidos);
        setTotalPedidos(todosLosPedidos.length);
      }
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  // Generar array de fechas entre dos fechas
  const generarFechasEntre = (inicio, fin) => {
    const fechas = [];
    const fechaActual = new Date(inicio + 'T00:00:00');
    const fechaFinal = new Date(fin + 'T00:00:00');

    while (fechaActual <= fechaFinal) {
      const year = fechaActual.getFullYear();
      const month = String(fechaActual.getMonth() + 1).padStart(2, '0');
      const day = String(fechaActual.getDate()).padStart(2, '0');
      fechas.push(`${year}-${month}-${day}`);

      fechaActual.setDate(fechaActual.getDate() + 1);
    }

    return fechas;
  };

  const handleImprimir = () => {
    window.print();
  };

  const exportarCSV = () => {
    if (estadisticas.length === 0) {
      alert('No hay datos para exportar');
      return;
    }

    // Crear CSV
    let csv = 'Categoría,Plato,Cantidad\n';
    estadisticas.forEach((item) => {
      csv += `${item.categoria},"${item.plato}",${item.cantidad}\n`;
    });
    csv += `\nTotal de pedidos,${totalPedidos}\n`;

    // Descargar
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const nombreArchivo = fechaInicio === fechaFin
      ? `produccion_${fechaInicio}.csv`
      : `produccion_${fechaInicio}_al_${fechaFin}.csv`;
    a.download = nombreArchivo;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Funciones para PDF
  const imprimirEtiquetasPDFHandler = () => {
    if (pedidosDetalle.length === 0) {
      alert('No hay pedidos para imprimir etiquetas');
      return;
    }

    imprimirEtiquetasPDF(pedidosDetalle);
  };

  const descargarPDFHandler = () => {
    if (pedidosDetalle.length === 0) {
      alert('No hay pedidos para descargar');
      return;
    }

    const nombreArchivo = fechaInicio === fechaFin
      ? `etiquetas_${fechaInicio}`
      : `etiquetas_${fechaInicio}_al_${fechaFin}`;
    descargarEtiquetasPDF(pedidosDetalle, nombreArchivo);
  };

  // Función para generar título según período
  const obtenerTituloPeriodo = () => {
    if (fechaInicio === fechaFin) {
      return formatearFecha(fechaInicio);
    }

    const [yearInicio, monthInicio, dayInicio] = fechaInicio.split('-');
    const [yearFin, monthFin, dayFin] = fechaFin.split('-');

    const fechaInicioObj = new Date(yearInicio, monthInicio - 1, dayInicio);
    const fechaFinObj = new Date(yearFin, monthFin - 1, dayFin);

    const formatoCorto = { day: 'numeric', month: 'short' };
    const formatoLargo = { day: 'numeric', month: 'long', year: 'numeric' };

    if (yearInicio === yearFin && monthInicio === monthFin) {
      // Mismo mes
      return `${dayInicio} al ${fechaFinObj.toLocaleDateString('es-AR', formatoLargo)}`;
    } else if (yearInicio === yearFin) {
      // Mismo año
      return `${fechaInicioObj.toLocaleDateString('es-AR', formatoCorto)} al ${fechaFinObj.toLocaleDateString('es-AR', formatoLargo)}`;
    } else {
      // Diferente año
      return `${fechaInicioObj.toLocaleDateString('es-AR', formatoLargo)} al ${fechaFinObj.toLocaleDateString('es-AR', formatoLargo)}`;
    }
  };

  const fechaFormateada = fechaInicio ? obtenerTituloPeriodo() : '';

  // Agrupar por categoría
  const estadisticasPorCategoria = estadisticas.reduce((acc, item) => {
    if (!acc[item.categoria]) {
      acc[item.categoria] = [];
    }
    acc[item.categoria].push(item);
    return acc;
  }, {});

  const ordenCategorias = ['Entrada', 'Principal', 'Postre', 'Bebida'];

  return (
    <div>
      <div className="page-header no-print">
        <h1>Producción - Cocina</h1>
        <p>Orden de producción para la cocina</p>
      </div>

      {/* Tabs */}
      <div className="tabs no-print">
        <button
          className={`tab ${activeTab === 'hoy' ? 'active' : ''}`}
          onClick={() => setActiveTab('hoy')}
        >
          📅 Hoy
        </button>
        <button
          className={`tab ${activeTab === 'mañana' ? 'active' : ''}`}
          onClick={() => setActiveTab('mañana')}
        >
          📅 Mañana
        </button>
        <button
          className={`tab ${activeTab === 'semana' ? 'active' : ''}`}
          onClick={() => setActiveTab('semana')}
        >
          📆 Semana
        </button>
        <button
          className={`tab ${activeTab === 'quincena' ? 'active' : ''}`}
          onClick={() => setActiveTab('quincena')}
        >
          📋 Quincena
        </button>
        <button
          className={`tab ${activeTab === 'mes' ? 'active' : ''}`}
          onClick={() => setActiveTab('mes')}
        >
          📊 Mes
        </button>
      </div>

      {/* Botones de acción */}
      <div style={{ marginBottom: '20px' }} className="no-print">
        <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
          <button className="btn btn-primary" onClick={handleImprimir}>
            🖨️ Imprimir Remito
          </button>
          <button className="btn btn-success" onClick={exportarCSV}>
            📊 Exportar CSV
          </button>
          <button className="btn btn-secondary" onClick={cargarDatos}>
            🔄 Actualizar
          </button>
        </div>

        {/* Botones de Etiquetas Zebra - PDF (Recomendado) */}
        <div style={{ display: 'flex', gap: '10px', padding: '10px', background: '#d4edda', borderRadius: '8px', border: '2px solid #27ae60', marginBottom: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginRight: '10px' }}>
            <span style={{ fontWeight: 'bold', color: '#27ae60', fontSize: '14px' }}>📋 Etiquetas:</span>
          </div>
          <button
            className="btn btn-success"
            onClick={imprimirEtiquetasPDFHandler}
            style={{ fontSize: '13px', padding: '8px 12px' }}
            title="Abre ventana de impresión con etiquetas en PDF"
          >
            🖨️ Imprimir PDF
          </button>
          <button
            className="btn btn-primary"
            onClick={descargarPDFHandler}
            style={{ fontSize: '13px', padding: '8px 12px' }}
            title="Abre PDF en nueva ventana para guardar o imprimir después"
          >
            📄 Ver PDF
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Cargando datos de producción...</div>
      ) : (
        <>
          {estadisticas.length === 0 ? (
            <div className="card">
              <div style={{ textAlign: 'center', padding: '60px 20px', color: '#7f8c8d' }}>
                <h3 style={{ fontSize: '24px', marginBottom: '10px' }}>
                  No hay pedidos para {activeTab === 'hoy' ? 'hoy' :
                    activeTab === 'mañana' ? 'mañana' :
                    activeTab === 'semana' ? 'esta semana' :
                    activeTab === 'quincena' ? 'esta quincena' :
                    'este mes'}
                </h3>
                <p>Cuando haya pedidos confirmados aparecerán aquí</p>
              </div>
            </div>
          ) : (
            <>
              {/* Vista normal (pantalla) */}
              <div className="screen-only">
                {/* Encabezado */}
                <div className="card" style={{ marginBottom: '20px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <h1 style={{ fontSize: '32px', marginBottom: '10px' }}>
                      ORDEN DE PRODUCCIÓN - COCINA
                    </h1>
                    <h2 style={{ fontSize: '24px', color: '#27AE60', marginBottom: '10px' }}>
                      {fechaFormateada}
                    </h2>
                    {(activeTab === 'hoy' || activeTab === 'mañana') && (
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '30px', marginBottom: '10px' }}>
                        <div>
                          <p style={{ fontSize: '14px', color: '#7f8c8d', margin: '0' }}>Fecha de Producción:</p>
                          <p style={{ fontSize: '18px', color: '#2c3e50', fontWeight: 'bold', margin: '5px 0 0 0' }}>
                            {new Date(new Date().getTime() + (activeTab === 'mañana' ? 86400000 : 0)).toLocaleDateString('es-AR')}
                          </p>
                        </div>
                        <div>
                          <p style={{ fontSize: '14px', color: '#7f8c8d', margin: '0' }}>Fecha de Vencimiento:</p>
                          <p style={{ fontSize: '18px', color: '#e74c3c', fontWeight: 'bold', margin: '5px 0 0 0' }}>
                            {new Date(new Date().getTime() + (activeTab === 'mañana' ? 172800000 : 86400000)).toLocaleDateString('es-AR')}
                          </p>
                        </div>
                      </div>
                    )}
                    <p style={{ fontSize: '18px', color: '#7f8c8d' }}>
                      Total de pedidos: <strong>{totalPedidos}</strong>
                    </p>
                  </div>
                </div>

                {/* Resumen por categoría - Si es hoy o mañana, mostrar por fecha; si es rango, mostrar normal */}
                {(activeTab === 'hoy' || activeTab === 'mañana') ? (
                  // Un solo día (hoy o mañana)
                  ordenCategorias.map((categoria) => {
                    const items = estadisticasPorCategoria[categoria];
                    if (!items || items.length === 0) return null;

                    const totalCategoria = items.reduce((sum, item) => sum + item.cantidad, 0);

                    return (
                      <div key={categoria} className="card" style={{ marginBottom: '20px' }}>
                        <div
                          style={{
                            background: '#f8f9fa',
                            padding: '15px',
                            borderRadius: '5px',
                            marginBottom: '15px',
                          }}
                        >
                          <h2 style={{ fontSize: '24px', color: '#2c3e50', marginBottom: '5px' }}>
                            {categoria}
                          </h2>
                          <p style={{ color: '#7f8c8d', fontSize: '14px' }}>
                            Total: <strong>{totalCategoria}</strong> porciones
                          </p>
                        </div>

                        <table>
                          <thead>
                            <tr>
                              <th style={{ width: '70%' }}>Plato</th>
                              <th style={{ width: '30%', textAlign: 'center' }}>Cantidad</th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((item, idx) => (
                              <tr key={idx}>
                                <td>
                                  <strong style={{ fontSize: '16px' }}>{item.plato}</strong>
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                  <span
                                    style={{
                                      fontSize: '20px',
                                      fontWeight: 'bold',
                                      background: '#27AE60',
                                      color: 'white',
                                      padding: '8px 20px',
                                      borderRadius: '8px',
                                      display: 'inline-block',
                                    }}
                                  >
                                    {item.cantidad}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })
                ) : (
                  // Rango de fechas (semana, quincena, mes) - mostrar agregado por categoría
                  ordenCategorias.map((categoria) => {
                    const items = estadisticasPorCategoria[categoria];
                    if (!items || items.length === 0) return null;

                    const totalCategoria = items.reduce((sum, item) => sum + item.cantidad, 0);

                    return (
                      <div key={categoria} className="card" style={{ marginBottom: '20px' }}>
                        <div
                          style={{
                            background: '#f8f9fa',
                            padding: '15px',
                            borderRadius: '5px',
                            marginBottom: '15px',
                          }}
                        >
                          <h2 style={{ fontSize: '24px', color: '#2c3e50', marginBottom: '5px' }}>
                            {categoria}
                          </h2>
                          <p style={{ color: '#7f8c8d', fontSize: '14px' }}>
                            Total: <strong>{totalCategoria}</strong> porciones
                          </p>
                        </div>

                        <table>
                          <thead>
                            <tr>
                              <th style={{ width: '70%' }}>Plato</th>
                              <th style={{ width: '30%', textAlign: 'center' }}>Cantidad</th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((item, idx) => (
                              <tr key={idx}>
                                <td>
                                  <strong style={{ fontSize: '16px' }}>{item.plato}</strong>
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                  <span
                                    style={{
                                      fontSize: '20px',
                                      fontWeight: 'bold',
                                      background: '#27AE60',
                                      color: 'white',
                                      padding: '8px 20px',
                                      borderRadius: '8px',
                                      display: 'inline-block',
                                    }}
                                  >
                                    {item.cantidad}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })
                )}

                {/* Resumen total */}
                <div className="card" style={{ background: '#27AE60', color: 'white' }}>
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <h2 style={{ fontSize: '28px', marginBottom: '10px' }}>RESUMEN TOTAL</h2>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '20px',
                        marginTop: '20px',
                      }}
                    >
                      <div>
                        <p style={{ fontSize: '14px', opacity: 0.9 }}>Total Pedidos</p>
                        <p style={{ fontSize: '36px', fontWeight: 'bold' }}>{totalPedidos}</p>
                      </div>
                      <div>
                        <p style={{ fontSize: '14px', opacity: 0.9 }}>Total Platos Diferentes</p>
                        <p style={{ fontSize: '36px', fontWeight: 'bold' }}>{estadisticas.length}</p>
                      </div>
                      <div>
                        <p style={{ fontSize: '14px', opacity: 0.9 }}>Total Porciones</p>
                        <p style={{ fontSize: '36px', fontWeight: 'bold' }}>
                          {estadisticas.reduce((sum, item) => sum + item.cantidad, 0)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Lista de pedidos individual */}
                <div className="card" style={{ marginTop: '30px' }}>
                  <h2 style={{ marginBottom: '20px' }}>
                    Detalle de Pedidos Individuales ({pedidosDetalle.length})
                  </h2>
                  <table>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Usuario</th>
                        <th>DNI</th>
                        <th>Tipo</th>
                        <th>Platos Seleccionados</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pedidosDetalle.map((pedido, idx) => (
                        <tr key={pedido.id}>
                          <td>{idx + 1}</td>
                          <td>{pedido.usuario.nombre}</td>
                          <td>{pedido.usuario.dni}</td>
                          <td>
                            <span
                              style={{
                                fontSize: '11px',
                                padding: '3px 8px',
                                borderRadius: '10px',
                                background:
                                  pedido.tipoInterfaz === 'totem' ? '#9b59b6' : '#3498db',
                                color: 'white',
                              }}
                            >
                              {pedido.tipoInterfaz}
                            </span>
                          </td>
                          <td>
                            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px' }}>
                              {pedido.items.map((item, i) => (
                                <li key={i}>
                                  <strong>{item.categoria}:</strong> {item.plato}
                                </li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Vista de impresión (remito profesional) */}
              <div className="print-only">
                <div style={{ padding: '5px', fontFamily: 'Arial, sans-serif' }}>
                  {/* Encabezado del remito */}
                  <div style={{ borderBottom: '2px solid #000', paddingBottom: '6px', marginBottom: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <h1 style={{ fontSize: '18px', margin: '0 0 3px 0', fontWeight: 'bold' }}>
                          ORDEN DE PRODUCCIÓN
                        </h1>
                        <p style={{ fontSize: '11px', margin: '2px 0', color: '#555' }}>
                          Comedor Industrial
                        </p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        {(activeTab === 'hoy' || activeTab === 'mañana') ? (
                          // Un solo día
                          <>
                            <p style={{ fontSize: '9px', margin: '2px 0' }}>
                              <strong>Fecha de Producción:</strong>
                            </p>
                            <p style={{ fontSize: '13px', margin: '2px 0', fontWeight: 'bold' }}>
                              {new Date(new Date().getTime() + (activeTab === 'mañana' ? 86400000 : 0)).toLocaleDateString('es-AR')}
                            </p>
                            <p style={{ fontSize: '9px', margin: '6px 0 2px 0' }}>
                              <strong>Fecha de Vencimiento:</strong>
                            </p>
                            <p style={{ fontSize: '13px', margin: '2px 0', fontWeight: 'bold', color: '#cc0000' }}>
                              {new Date(new Date().getTime() + (activeTab === 'mañana' ? 172800000 : 86400000)).toLocaleDateString('es-AR')}
                            </p>
                          </>
                        ) : (
                          // Rango de fechas
                          <>
                            <p style={{ fontSize: '9px', margin: '2px 0' }}>
                              <strong>Período de Producción:</strong>
                            </p>
                            <p style={{ fontSize: '12px', margin: '2px 0', fontWeight: 'bold' }}>
                              {new Date(fechaInicio).toLocaleDateString('es-AR')} a {new Date(fechaFin).toLocaleDateString('es-AR')}
                            </p>
                            <p style={{ fontSize: '8px', margin: '4px 0 2px 0', color: '#666' }}>
                              Cada día se produce y vence al día siguiente
                            </p>
                          </>
                        )}
                        <p style={{ fontSize: '8px', margin: '6px 0 0 0', color: '#666' }}>
                          Generado: {new Date().toLocaleString('es-AR')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Resumen rápido */}
                  <div style={{ background: '#f5f5f5', padding: '8px', marginBottom: '10px', border: '2px solid #333' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
                      <div>
                        <p style={{ fontSize: '9px', margin: '0 0 2px 0', textTransform: 'uppercase', color: '#666' }}>
                          Total Pedidos
                        </p>
                        <p style={{ fontSize: '20px', margin: '0', fontWeight: 'bold' }}>
                          {totalPedidos}
                        </p>
                      </div>
                      <div>
                        <p style={{ fontSize: '9px', margin: '0 0 2px 0', textTransform: 'uppercase', color: '#666' }}>
                          Platos Diferentes
                        </p>
                        <p style={{ fontSize: '20px', margin: '0', fontWeight: 'bold' }}>
                          {estadisticas.length}
                        </p>
                      </div>
                      <div>
                        <p style={{ fontSize: '9px', margin: '0 0 2px 0', textTransform: 'uppercase', color: '#666' }}>
                          Total Porciones
                        </p>
                        <p style={{ fontSize: '20px', margin: '0', fontWeight: 'bold' }}>
                          {estadisticas.reduce((sum, item) => sum + item.cantidad, 0)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Detalle por categoría - Si es hoy o mañana, mostrar por fecha; si es rango, mostrar normal */}
                  {(activeTab === 'hoy' || activeTab === 'mañana') ? (
                    // Un solo día (hoy o mañana)
                    ordenCategorias.map((categoria) => {
                      const items = estadisticasPorCategoria[categoria];
                      if (!items || items.length === 0) return null;

                      const totalCategoria = items.reduce((sum, item) => sum + item.cantidad, 0);

                      return (
                        <div key={categoria} style={{ marginBottom: '8px', pageBreakInside: 'avoid' }}>
                          <div style={{ background: '#000', color: '#fff', padding: '4px 8px', marginBottom: '3px' }}>
                            <h2 style={{ fontSize: '14px', margin: '0', textTransform: 'uppercase', display: 'flex', justifyContent: 'space-between' }}>
                              <span>{categoria}</span>
                              <span>Total: {totalCategoria} porciones</span>
                            </h2>
                          </div>

                          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '5px' }}>
                            <thead>
                              <tr style={{ background: '#f0f0f0' }}>
                                <th style={{ border: '1px solid #ddd', padding: '4px 6px', textAlign: 'left', fontSize: '11px' }}>
                                  Plato
                                </th>
                                <th style={{ border: '1px solid #ddd', padding: '4px 6px', textAlign: 'center', fontSize: '11px', width: '100px' }}>
                                  Cantidad
                                </th>
                                <th style={{ border: '1px solid #ddd', padding: '4px 6px', textAlign: 'center', fontSize: '11px', width: '70px' }}>
                                  Listo ✓
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {items.map((item, idx) => (
                                <tr key={idx}>
                                  <td style={{ border: '1px solid #ddd', padding: '4px 6px', fontSize: '11px' }}>
                                    {item.plato}
                                  </td>
                                  <td style={{ border: '1px solid #ddd', padding: '4px 6px', textAlign: 'center' }}>
                                    <span style={{
                                      fontSize: '16px',
                                      fontWeight: 'bold',
                                      background: '#000',
                                      color: '#fff',
                                      padding: '3px 10px',
                                      borderRadius: '3px',
                                      display: 'inline-block'
                                    }}>
                                      {item.cantidad}
                                    </span>
                                  </td>
                                  <td style={{ border: '1px solid #ddd', padding: '4px 6px', textAlign: 'center', background: '#f9f9f9' }}>
                                    {/* Checkbox para marcar */}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    })
                  ) : (
                    // Rango de fechas (semana, quincena, mes) - mostrar agregado por categoría
                    ordenCategorias.map((categoria) => {
                      const items = estadisticasPorCategoria[categoria];
                      if (!items || items.length === 0) return null;

                      const totalCategoria = items.reduce((sum, item) => sum + item.cantidad, 0);

                      return (
                        <div key={categoria} style={{ marginBottom: '8px', pageBreakInside: 'avoid' }}>
                          <div style={{ background: '#000', color: '#fff', padding: '4px 8px', marginBottom: '3px' }}>
                            <h2 style={{ fontSize: '14px', margin: '0', textTransform: 'uppercase', display: 'flex', justifyContent: 'space-between' }}>
                              <span>{categoria}</span>
                              <span>Total: {totalCategoria} porciones</span>
                            </h2>
                          </div>

                          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '5px' }}>
                            <thead>
                              <tr style={{ background: '#f0f0f0' }}>
                                <th style={{ border: '1px solid #ddd', padding: '4px 6px', textAlign: 'left', fontSize: '11px' }}>
                                  Plato
                                </th>
                                <th style={{ border: '1px solid #ddd', padding: '4px 6px', textAlign: 'center', fontSize: '11px', width: '100px' }}>
                                  Cantidad
                                </th>
                                <th style={{ border: '1px solid #ddd', padding: '4px 6px', textAlign: 'center', fontSize: '11px', width: '70px' }}>
                                  Listo ✓
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {items.map((item, idx) => (
                                <tr key={idx}>
                                  <td style={{ border: '1px solid #ddd', padding: '4px 6px', fontSize: '11px' }}>
                                    {item.plato}
                                  </td>
                                  <td style={{ border: '1px solid #ddd', padding: '4px 6px', textAlign: 'center' }}>
                                    <span style={{
                                      fontSize: '16px',
                                      fontWeight: 'bold',
                                      background: '#000',
                                      color: '#fff',
                                      padding: '3px 10px',
                                      borderRadius: '3px',
                                      display: 'inline-block'
                                    }}>
                                      {item.cantidad}
                                    </span>
                                  </td>
                                  <td style={{ border: '1px solid #ddd', padding: '4px 6px', textAlign: 'center', background: '#f9f9f9' }}>
                                    {/* Checkbox para marcar */}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    })
                  )}

                  {/* Pie de página */}
                  <div style={{ marginTop: '10px', borderTop: '1px solid #000', paddingTop: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <div style={{ width: '45%' }}>
                        <p style={{ fontSize: '9px', marginBottom: '10px', margin: '0 0 10px 0' }}>FIRMA COCINA:</p>
                        <div style={{ borderBottom: '1px solid #000', marginBottom: '2px' }}>&nbsp;</div>
                        <p style={{ fontSize: '8px', color: '#666', margin: '2px 0 0 0' }}>Aclaración</p>
                      </div>
                      <div style={{ width: '45%' }}>
                        <p style={{ fontSize: '9px', marginBottom: '10px', margin: '0 0 10px 0' }}>FIRMA CONTROL:</p>
                        <div style={{ borderBottom: '1px solid #000', marginBottom: '2px' }}>&nbsp;</div>
                        <p style={{ fontSize: '8px', color: '#666', margin: '2px 0 0 0' }}>Aclaración</p>
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: '5px', textAlign: 'center', fontSize: '7px', color: '#999' }}>
                    <p style={{ margin: '0' }}>MealPoint - Absolute Zero</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Estilos de impresión */}
      <style>{`
        @media screen {
          .print-only {
            display: none !important;
          }
          .screen-only {
            display: block !important;
          }
        }

        @media print {
          .no-print, .screen-only {
            display: none !important;
          }

          .print-only {
            display: block !important;
          }

          body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
          }

          * {
            print-color-adjust: exact !important;
            -webkit-print-color-adjust: exact !important;
          }

          @page {
            margin: 1cm;
            size: A4;
          }

          table {
            page-break-inside: avoid !important;
          }

          h1, h2 {
            page-break-after: avoid !important;
          }
        }
      `}</style>
    </div>
  );
}

export default Produccion;
